/* eslint-disable no-restricted-syntax */
import moment, { Moment } from 'moment';
import { toast } from 'react-toastify';
import _ from 'lodash';
//
import { IStoreState } from 'store';
import { IDealProduct, IStatusFieldOption } from 'store/field-options/types';
import { IParetoProduct, IParetoProductWithDetails } from 'store/pareto/types';
import { calculateStartAndEndValues, generateProductPeriodString } from 'lib/helpers/date-range';
import { mapNameToDisplayName } from 'lib/helpers/field-options';
import messages from 'constants/messages';
import { ECostFormulaParamTypeLabel, EForecastEvaluationTypeLabel } from 'store/deal-evaluation/types';
import { EDisplay, EDisplayLabel } from 'components/deal-results/product-items';
import { IDeal } from 'store/deals/types';

const throttleDuration = 100;

// Workaround: Pareto is updated multiple times (triggered by irrelevant store updates)
const debouncedWarning = _.throttle(
  () => toast.warn(messages.warnings.removedProductsFromPareto, { autoClose: 2000 }),
  throttleDuration
);

function getDealId(dealData: IDeal) {
  return dealData.id ?? '';
}

function getDealCustomer(dealData: IDeal) {
  return dealData.customer ?? '';
}

const paretoMap = (paretoDealProduct: IParetoProductWithDetails, statuses: IStatusFieldOption[]) => {
  let color = 'grey';
  statuses.forEach(status => {
    if (paretoDealProduct.status === status.name) {
      color = status.color;
    }
  });
  return {
    ...paretoDealProduct,
    color,
  };
};

function validateSelectedDisplay(
  a: IParetoProductWithDetails,
  b: IParetoProductWithDetails,
  selectedDisplay: EDisplay
) {
  return b[selectedDisplay] === a[selectedDisplay];
}

function returnSelectedDisplay(a: IParetoProductWithDetails, b: IParetoProductWithDetails, selectedDisplay: EDisplay) {
  return (b[selectedDisplay] ?? 0) - (a[selectedDisplay] ?? 0);
}

function returnMonthlyVolumn(a: IParetoProductWithDetails, b: IParetoProductWithDetails) {
  return (a.monthlyVolume ?? 0) - (b.monthlyVolume ?? 0);
}

function validatePareto(paretoDealProduct: IParetoProduct, selectedDisplay: string | number) {
  return paretoDealProduct[selectedDisplay] !== null && paretoDealProduct.monthlyVolume !== null;
}

function processUtill(deal: IDeal, paretoDealId: string, products: IDealProduct[]) {
  if (paretoDealId === deal.id) {
    if (!deal.products) {
      return true;
    }
    products.push(...deal.products);
  }
  return false;
}

// Run through all deals in pareto payload and extract products
export const getParetoDealProducts = (state: IStoreState): IParetoProductWithDetails[] => {
  const paretoDealProducts: IParetoProductWithDetails[] = [];
  const { pareto, fieldOptions, dealComparison, deals } = state;
  const { paretoDeals } = pareto;
  const { statuses, regions, productLines, products } = fieldOptions;
  const { selectedDisplay } = dealComparison;
  const { data } = deals;

  if (!paretoDeals.length) {
    return paretoDealProducts;
  }

  let hasDealProductsRemoved = false;

  paretoDeals.forEach(paretoDeal => {
    if (!paretoDeal.products) {
      return;
    }

    // map deal data to paretoDeal evaluation
    const dealDataArray = data.filter(d => d.name === paretoDeal.name);
    if (!dealDataArray.length) {
      return;
    }
    const dealData = dealDataArray[0];
    const startAndEndValues = calculateStartAndEndValues(dealData.products);
    if (!startAndEndValues) {
      return;
    }

    const region = mapNameToDisplayName(dealData.region, regions);
    const productLine = mapNameToDisplayName(dealData.productLine, productLines);

    paretoDeal.products.forEach(paretoDealProduct => {
      if (validatePareto(paretoDealProduct, selectedDisplay)) {
        paretoDealProducts.push({
          ...paretoDealProduct,
          name: mapNameToDisplayName(paretoDealProduct.name, products),
          color: '',
          status: paretoDeal.status,
          dealId: getDealId(dealData),
          dealName: paretoDeal.name,
          regionProductLine: `${region}, ${productLine}`,
          customer: getDealCustomer(dealData),
          dateRange: generateProductPeriodString(startAndEndValues.start, startAndEndValues.end),
        });
      } else {
        hasDealProductsRemoved = true;
      }
    });
  });

  if (hasDealProductsRemoved) {
    debouncedWarning();
  }

  // sort descending by selected price (e.g., base, netback, sales, margin)
  paretoDealProducts.sort((a, b) => {
    if (validateSelectedDisplay(a, b, selectedDisplay)) {
      return returnMonthlyVolumn(a, b);
    }
    return returnSelectedDisplay(a, b, selectedDisplay);
  });

  // map colors to products using status value
  if (!statuses) {
    return paretoDealProducts;
  }
  return paretoDealProducts.map(paretoDealProduct => {
    return paretoMap(paretoDealProduct, statuses);
  });
};

// "Pareto, Netback, IHS Forecast, 06/2020 - 12/2020"
export const getParetoLegendText = (state: IStoreState): string => {
  const { pareto: paretoState, deals: dealState, dealComparison, dealEvaluation } = state;
  const { paretoDealIds } = paretoState;
  const { data: deals } = dealState;
  const { selectedDisplay } = dealComparison;
  const { selectedForecastType, selectedCostFormulaType } = dealEvaluation;

  const products: IDealProduct[] = [];
  paretoDealIds.forEach(paretoDealId => {
    deals.some(deal => {
      return processUtill(deal, paretoDealId, products);
    });
  });

  let beginning: Moment | null = null;
  let end: Moment | null = null;
  products.forEach((product: IDealProduct) => {
    const { startDate, endDate } = product;
    if (!startDate || !endDate) {
      return;
    }
    const startMoment = moment(startDate, 'MM/YYYY');
    const endMoment = moment(endDate, 'MM/YYYY');
    if (!beginning) {
      beginning = startMoment;
    }
    if (!end) {
      end = endMoment;
    }
    if (startMoment.isBefore(beginning)) {
      beginning = startMoment;
    }
    if (endMoment.isAfter(end)) {
      end = endMoment;
    }
  });

  let str = `Pareto, ${EDisplayLabel[selectedDisplay]}, ${EForecastEvaluationTypeLabel[selectedForecastType]} forecast, ${ECostFormulaParamTypeLabel[selectedCostFormulaType]} cost basis`;
  if (beginning && end) {
    // @ts-ignore
    str = str.concat(`, ${beginning.format('MM/YYYY')} - ${end.format('MM/YYYY')}`);
  }
  return str;
};

// Gets the currently active statuses to populate the legend status list
export const getStatusesForLegend = (state: IStoreState): IStatusFieldOption[] => {
  const statusesForLegend: IStatusFieldOption[] = [];

  const { pareto, fieldOptions } = state;
  const { statuses } = fieldOptions;
  const { paretoDeals } = pareto;

  if (!statuses || !paretoDeals) {
    return statusesForLegend;
  }

  paretoDeals.forEach(paretoDeal => {
    for (const statuse of statuses) {
      if (statuse.name === paretoDeal.status) {
        const existingStatuses = statusesForLegend.map(statusForLegend => statusForLegend.name);
        const indexOf = existingStatuses.indexOf(paretoDeal.status);
        if (indexOf === -1) {
          statusesForLegend.push(statuse);
        }
        break;
      }
    }
  });

  return statusesForLegend;
};
